/**
 * ChatThemeSettings — per-conversation appearance editor.
 *
 * Slim dark panel matching the feed aesthetic. A single live preview
 * stays pinned to the top while sections below tweak accent, background,
 * bubble shape, and layout. Backgrounds are applied directly when
 * picked — no per-swatch preview card.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Loader2, Palette, RotateCcw, Sparkles, Upload, X } from "lucide-react";
import { uploadAvatar } from "@slumbr/core";
import { cn } from "@slumbr/ui";

import type { ChatTheme, BubbleStyle, LayoutMode, BackgroundType } from "../types";

const ACCENT_PRESETS = [
  "#ff6b8f",
  "#c83050",
  "#7c5cff",
  "#5c7cfa",
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#f97316",
  "#a855f7",
  "#ec4899",
  "#0c8599",
  "#e03131",
];

const BG_SOLID_PRESETS = [
  "#0b0d0f",
  "#111417",
  "#1a1b1e",
  "#1b2838",
  "#1e1433",
  "#1a2e1a",
  "#f5f0eb",
  "#eef1f5",
  "#f3f0ff",
];

const OVERLAY_COLOR_PRESETS = [
  "#000000",
  "#0b0d0f",
  "#1a1b1e",
  "#1b2838",
  "#ffffff",
  "#ff6b8f",
  "#7c5cff",
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#f97316",
  "#a855f7",
];

const GRADIENT_PRESETS = [
  "linear-gradient(135deg, #c83050 0%, #5a1728 100%)",
  "linear-gradient(135deg, #ff6b8f 0%, #7d1932 100%)",
  "linear-gradient(135deg, #0a1a2f 0%, #1d4a5d 100%)",
  "linear-gradient(135deg, #240202 0%, #ff3d6b 100%)",
  "linear-gradient(135deg, #1a0d2e 0%, #ff6ad5 100%)",
  "linear-gradient(135deg, #0f0c29 0%, #302b63 100%)",
  "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
  "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
];

const THEME_PRESETS: Array<{ id: string; name: string; theme: ChatTheme }> = [
  { id: "default", name: "Default", theme: {} },
  {
    id: "rose",
    name: "Midnight Rose",
    theme: {
      accentColor: "#ff6b8f",
      backgroundType: "gradient",
      backgroundGradient: "linear-gradient(135deg, #4a0f1f 0%, #ff6b8f 100%)",
      layoutMode: "bubbles",
      bubbleStyle: "rounded",
    },
  },
  {
    id: "ocean",
    name: "Ocean",
    theme: {
      accentColor: "#0ea5e9",
      backgroundType: "gradient",
      backgroundGradient: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
      layoutMode: "bubbles",
      bubbleStyle: "rounded",
    },
  },
  {
    id: "forest",
    name: "Forest",
    theme: {
      accentColor: "#10b981",
      backgroundType: "solid",
      backgroundColor: "#064e3b",
      layoutMode: "bubbles",
      bubbleStyle: "rounded",
    },
  },
  {
    id: "mono",
    name: "Mono",
    theme: {
      accentColor: "#6b7280",
      backgroundType: "solid",
      backgroundColor: "#1f2937",
      layoutMode: "irc",
    },
  },
];

function detectActivePreset(theme: ChatTheme | undefined): string {
  if (!theme || Object.keys(theme).length === 0) return "default";
  const target = JSON.stringify(theme);
  for (const p of THEME_PRESETS) {
    if (JSON.stringify(p.theme) === target) return p.id;
  }
  return "custom";
}

interface ChatThemeSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  theme: ChatTheme | undefined;
  onThemeChange: (theme: ChatTheme | undefined) => void;
}

type SectionId = "theme" | "accent" | "background" | "bubbles" | "layout";

export function ChatThemeSettings({ isOpen, onClose, theme, onThemeChange }: ChatThemeSettingsProps) {
  const [localTheme, setLocalTheme] = useState<ChatTheme>(theme ?? {});
  const [section, setSection] = useState<SectionId>("theme");
  const [customAccent, setCustomAccent] = useState(theme?.accentColor ?? "");
  const [imageUrl, setImageUrl] = useState(theme?.backgroundImage ?? "");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const themeJson = JSON.stringify(theme ?? {});
  useEffect(() => {
    setLocalTheme(theme ?? {});
    setCustomAccent(theme?.accentColor ?? "");
    setImageUrl(theme?.backgroundImage ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeJson]);

  const updateTheme = useCallback(
    (updates: Partial<ChatTheme>) => {
      const next = { ...localTheme, ...updates };
      setLocalTheme(next);
      const hasAnyValue = Object.values(next).some((v) => v !== undefined && v !== "");
      onThemeChange(hasAnyValue ? next : undefined);
    },
    [localTheme, onThemeChange]
  );

  const handleReset = useCallback(() => {
    setLocalTheme({});
    setCustomAccent("");
    setImageUrl("");
    onThemeChange(undefined);
  }, [onThemeChange]);

  const activePreset = useMemo(() => detectActivePreset(localTheme), [localTheme]);

  async function handleUpload(file: File) {
    setUploading(true);
    setUploadError("");
    try {
      const url = await uploadAvatar(file);
      setImageUrl(url);
      updateTheme({ backgroundType: "image", backgroundImage: url });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={onClose} />

      <div className="animate-slide-up relative z-10 flex max-h-[90dvh] w-full max-w-md flex-col overflow-hidden rounded-t-3xl border border-white/[0.06] bg-[#0b0d0f] pb-[env(safe-area-inset-bottom)] sm:pb-0 text-[#e7e9ea] shadow-2xl shadow-black/70 sm:rounded-3xl">
        <header className="flex items-center justify-between border-b border-white/[0.06] px-5 py-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#ff6b8f]" />
            <h3 className="text-sm font-black tracking-wide text-[#e7e9ea]">Chat appearance</h3>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleReset}
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-[#71767b] transition hover:bg-white/[0.05] hover:text-[#e7e9ea]"
              title="Reset to default"
            >
              <RotateCcw className="h-3 w-3" />
              Reset
            </button>
            <button
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-full text-[#71767b] transition hover:bg-white/[0.05] hover:text-[#e7e9ea]"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>

        <LivePreview theme={localTheme} />

        <nav className="flex items-center gap-1 overflow-x-auto border-b border-white/[0.04] bg-[#0b0d0f]/85 px-3 py-2 backdrop-blur-xl [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {(["theme", "accent", "background", "bubbles", "layout"] as SectionId[]).map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => setSection(id)}
              className={cn(
                "shrink-0 rounded-full px-3 py-1.5 text-[11px] font-black uppercase tracking-wide transition",
                section === id
                  ? "bg-[#ff477e]/15 text-[#ff8fac]"
                  : "text-[#71767b] hover:bg-white/[0.04] hover:text-[#d7dbdc]"
              )}
            >
              {id}
            </button>
          ))}
        </nav>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {section === "theme" && (
            <Block label="Bundled themes">
              <div className="grid grid-cols-3 gap-2">
                {THEME_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => {
                      const next = { ...preset.theme };
                      setLocalTheme(next);
                      setCustomAccent(next.accentColor ?? "");
                      setImageUrl(next.backgroundImage ?? "");
                      onThemeChange(Object.keys(next).length > 0 ? next : undefined);
                    }}
                    className={cn(
                      "group rounded-2xl border p-1 transition",
                      activePreset === preset.id
                        ? "border-[#ff6b8f]/50 bg-[#ff477e]/[0.08]"
                        : "border-white/[0.06] hover:border-white/[0.15]"
                    )}
                  >
                    <span
                      className="block h-14 w-full rounded-xl"
                      style={previewSwatchStyle(preset.theme)}
                    />
                    <span className="mt-1.5 block text-center text-[10px] font-black uppercase tracking-wide text-[#d7dbdc]">
                      {preset.name}
                    </span>
                  </button>
                ))}
              </div>
            </Block>
          )}

          {section === "accent" && (
            <Block label="Accent color">
              <div className="grid grid-cols-6 gap-2">
                {ACCENT_PRESETS.map((hex) => {
                  const selected = (localTheme.accentColor ?? "") === hex;
                  return (
                    <button
                      key={hex}
                      type="button"
                      onClick={() => updateTheme({ accentColor: hex })}
                      className={cn(
                        "relative h-8 rounded-full border transition",
                        selected
                          ? "border-white scale-105 shadow-[0_0_0_2px_rgba(255,107,143,0.6)]"
                          : "border-white/[0.1] hover:scale-105"
                      )}
                      style={{ backgroundColor: hex }}
                      aria-label={hex}
                    />
                  );
                })}
              </div>
              <div className="mt-3 flex items-center gap-2 border-t border-white/[0.04] pt-3">
                <span className="text-[10px] font-black uppercase tracking-[0.18em] text-[#536471]">Custom</span>
                <input
                  type="color"
                  value={customAccent || "#ff6b8f"}
                  onChange={(event) => {
                    setCustomAccent(event.target.value);
                    updateTheme({ accentColor: event.target.value });
                  }}
                  className="h-7 w-10 cursor-pointer rounded border border-white/[0.08] bg-transparent"
                />
                <input
                  type="text"
                  value={customAccent}
                  onChange={(event) => setCustomAccent(event.target.value)}
                  onBlur={() => {
                    if (/^#[0-9a-fA-F]{6}$/.test(customAccent)) updateTheme({ accentColor: customAccent });
                  }}
                  placeholder="#ff6b8f"
                  className="flex-1 rounded-lg border border-white/[0.06] bg-black/30 px-3 py-1.5 text-sm text-[#e7e9ea] outline-none transition focus:border-[#ff6b8f]/40"
                />
              </div>
            </Block>
          )}

          {section === "background" && (
            <>
              <Block label="Type">
                <div className="flex items-center gap-1">
                  {(["solid", "gradient", "image"] as BackgroundType[]).map((type) => {
                    const active = (localTheme.backgroundType ?? "solid") === type;
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => updateTheme({ backgroundType: type })}
                        className={cn(
                          "rounded-full px-3 py-1.5 text-[11px] font-black uppercase tracking-wide transition",
                          active
                            ? "bg-[#ff477e]/15 text-[#ff8fac]"
                            : "text-[#71767b] hover:bg-white/[0.04] hover:text-[#d7dbdc]"
                        )}
                      >
                        {type}
                      </button>
                    );
                  })}
                </div>
              </Block>

              {(localTheme.backgroundType ?? "solid") === "solid" && (
                <Block label="Solid color">
                  <div className="grid grid-cols-6 gap-2">
                    {BG_SOLID_PRESETS.map((hex) => {
                      const selected = (localTheme.backgroundColor ?? "") === hex;
                      return (
                        <button
                          key={hex}
                          type="button"
                          onClick={() => updateTheme({ backgroundColor: hex })}
                          className={cn(
                            "h-8 rounded-lg border transition",
                            selected
                              ? "border-[#ff6b8f] shadow-[0_0_0_1px_rgba(255,107,143,0.4)]"
                              : "border-white/[0.08] hover:border-white/[0.15]"
                          )}
                          style={{ backgroundColor: hex }}
                          aria-label={hex}
                        />
                      );
                    })}
                  </div>
                  <input
                    type="text"
                    value={localTheme.backgroundColor ?? ""}
                    onChange={(event) => updateTheme({ backgroundColor: event.target.value })}
                    placeholder="#0b0d0f"
                    className="mt-3 w-full rounded-lg border border-white/[0.06] bg-black/30 px-3 py-1.5 text-sm text-[#e7e9ea] outline-none transition focus:border-[#ff6b8f]/40"
                  />
                </Block>
              )}

              {localTheme.backgroundType === "gradient" && (
                <Block label="Gradient">
                  <div className="grid grid-cols-4 gap-2">
                    {GRADIENT_PRESETS.map((css) => {
                      const selected = (localTheme.backgroundGradient ?? "") === css;
                      return (
                        <button
                          key={css}
                          type="button"
                          onClick={() => updateTheme({ backgroundGradient: css })}
                          className={cn(
                            "h-10 rounded-lg border transition",
                            selected
                              ? "border-[#ff6b8f] shadow-[0_0_0_1px_rgba(255,107,143,0.4)]"
                              : "border-white/[0.08] hover:border-white/[0.15]"
                          )}
                          style={{ background: css }}
                        />
                      );
                    })}
                  </div>
                </Block>
              )}

              {localTheme.backgroundType === "image" && (
                <>
                  <Block label="Image">
                    <input
                      type="text"
                      value={imageUrl}
                      onChange={(event) => setImageUrl(event.target.value)}
                      onBlur={() => updateTheme({ backgroundImage: imageUrl })}
                      placeholder="https://…"
                      className="w-full rounded-lg border border-white/[0.06] bg-black/30 px-3 py-2 text-sm text-[#e7e9ea] outline-none transition focus:border-[#ff6b8f]/40"
                    />
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#c83050] px-3 py-1.5 text-[11px] font-black text-white transition hover:bg-[#d63b5d] disabled:opacity-60"
                      >
                        {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                        Upload
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (file) void handleUpload(file);
                          if (fileInputRef.current) fileInputRef.current.value = "";
                        }}
                      />
                      {uploadError && <p className="text-[11px] text-red-300">{uploadError}</p>}
                    </div>
                  </Block>
                  <Block label="Overlay">
                    <p className="-mt-1 mb-2.5 text-[11px] text-[#71767b]">
                      Tint the background with a color to make messages easier to read.
                    </p>
                    <div className="grid grid-cols-6 gap-2">
                      {OVERLAY_COLOR_PRESETS.map((hex) => {
                        const selected = (localTheme.backgroundOverlayColor ?? "") === hex;
                        return (
                          <button
                            key={hex}
                            type="button"
                            onClick={() => {
                              updateTheme({
                                backgroundOverlayColor: hex,
                                backgroundOverlayOpacity: localTheme.backgroundOverlayOpacity ?? 40,
                              });
                            }}
                            className={cn(
                              "h-8 rounded-lg border transition",
                              selected
                                ? "border-[#ff6b8f] shadow-[0_0_0_1px_rgba(255,107,143,0.4)]"
                                : "border-white/[0.08] hover:border-white/[0.15]"
                            )}
                            style={{ backgroundColor: hex }}
                            aria-label={hex}
                          />
                        );
                      })}
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <span className="text-[10px] font-black uppercase tracking-[0.18em] text-[#536471]">Custom</span>
                      <input
                        type="color"
                        value={localTheme.backgroundOverlayColor ?? "#000000"}
                        onChange={(event) =>
                          updateTheme({
                            backgroundOverlayColor: event.target.value,
                            backgroundOverlayOpacity: localTheme.backgroundOverlayOpacity ?? 40,
                          })
                        }
                        className="h-7 w-10 cursor-pointer rounded border border-white/[0.08] bg-transparent"
                      />
                      <input
                        type="text"
                        value={localTheme.backgroundOverlayColor ?? ""}
                        onChange={(event) =>
                          updateTheme({ backgroundOverlayColor: event.target.value })
                        }
                        placeholder="#000000"
                        className="flex-1 rounded-lg border border-white/[0.06] bg-black/30 px-3 py-1.5 text-sm text-[#e7e9ea] outline-none transition focus:border-[#ff6b8f]/40"
                      />
                    </div>
                    <div className="mt-4">
                      <div className="mb-1.5 flex items-center justify-between">
                        <span className="text-[10px] font-black uppercase tracking-[0.18em] text-[#536471]">
                          Opacity
                        </span>
                        <span className="text-[11px] font-bold tabular-nums text-[#d7dbdc]">
                          {localTheme.backgroundOverlayOpacity ?? 0}%
                        </span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={1}
                        value={localTheme.backgroundOverlayOpacity ?? 0}
                        onChange={(event) => {
                          const next = Number(event.target.value);
                          updateTheme({
                            backgroundOverlayOpacity: next,
                            backgroundOverlayColor:
                              next > 0 && !localTheme.backgroundOverlayColor
                                ? "#000000"
                                : localTheme.backgroundOverlayColor,
                          });
                        }}
                        className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-white/[0.08] accent-[#ff6b8f] [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#ff6b8f] [&::-webkit-slider-thumb]:shadow-[0_0_0_2px_rgba(11,13,15,1)]"
                      />
                      {(localTheme.backgroundOverlayOpacity ?? 0) > 0 && (
                        <button
                          type="button"
                          onClick={() =>
                            updateTheme({
                              backgroundOverlayColor: undefined,
                              backgroundOverlayOpacity: undefined,
                            })
                          }
                          className="mt-2 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-[#71767b] transition hover:bg-white/[0.05] hover:text-[#e7e9ea]"
                        >
                          <RotateCcw className="h-3 w-3" />
                          Clear overlay
                        </button>
                      )}
                    </div>
                  </Block>
                </>
              )}
            </>
          )}

          {section === "bubbles" && (
            <Block label="Bubble shape">
              <div className="flex items-center gap-1">
                {(["rounded", "square", "minimal"] as BubbleStyle[]).map((style) => {
                  const active = (localTheme.bubbleStyle ?? "rounded") === style;
                  return (
                    <button
                      key={style}
                      type="button"
                      onClick={() => updateTheme({ bubbleStyle: style })}
                      className={cn(
                        "flex-1 rounded-full px-3 py-1.5 text-[11px] font-black uppercase tracking-wide transition",
                        active
                          ? "bg-[#ff477e]/15 text-[#ff8fac]"
                          : "text-[#71767b] hover:bg-white/[0.04] hover:text-[#d7dbdc]"
                      )}
                    >
                      {style}
                    </button>
                  );
                })}
              </div>
            </Block>
          )}

          {section === "layout" && (
            <Block label="Layout mode">
              <div className="flex items-center gap-1">
                {(["bubbles", "irc"] as LayoutMode[]).map((mode) => {
                  const active = (localTheme.layoutMode ?? "bubbles") === mode;
                  const label = mode === "irc" ? "Compact" : "Bubbles";
                  return (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => updateTheme({ layoutMode: mode })}
                      className={cn(
                        "flex-1 rounded-full px-3 py-1.5 text-[11px] font-black uppercase tracking-wide transition",
                        active
                          ? "bg-[#ff477e]/15 text-[#ff8fac]"
                          : "text-[#71767b] hover:bg-white/[0.04] hover:text-[#d7dbdc]"
                      )}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </Block>
          )}
        </div>
      </div>
    </div>
  );
}

function Block({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="border-b border-white/[0.04] px-5 py-4 last:border-b-0">
      <p className="mb-3 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-[#536471]">
        <Palette className="h-3 w-3 text-[#ff6b8f]" />
        {label}
      </p>
      {children}
    </section>
  );
}

function LivePreview({ theme }: { theme: ChatTheme }) {
  const accent = theme.accentColor ?? "#ff6b8f";
  const bubbleStyle = theme.bubbleStyle ?? "rounded";
  const layoutMode = theme.layoutMode ?? "bubbles";
  const radiusOwn =
    bubbleStyle === "square"
      ? "rounded-md"
      : bubbleStyle === "minimal"
        ? ""
        : "rounded-2xl rounded-br-md";
  const radiusOther =
    bubbleStyle === "square"
      ? "rounded-md"
      : bubbleStyle === "minimal"
        ? ""
        : "rounded-2xl rounded-bl-md";

  return (
    <div
      className="flex h-36 flex-col justify-end gap-2 border-b border-white/[0.06] p-4"
      style={getBackgroundStyle(theme) ?? { background: "#0b0d0f" }}
    >
      {layoutMode === "irc" ? (
        <>
          <p className="text-[11px] font-bold" style={{ color: accent }}>
            Jamie
          </p>
          <p className="-mt-1 text-xs text-white/90 [text-shadow:0_1px_2px_rgba(0,0,0,0.6)]">
            Hey! How's the new theme?
          </p>
          <p className="text-[11px] font-bold" style={{ color: "#fff" }}>
            You
          </p>
          <p className="-mt-1 text-xs text-white/90 [text-shadow:0_1px_2px_rgba(0,0,0,0.6)]">
            Loving the colors 💫
          </p>
        </>
      ) : (
        <>
          <div className="flex">
            <div className={cn("max-w-[70%] bg-white/85 px-3 py-1.5 text-xs text-black shadow-sm", radiusOther)}>
              Hey! How's the new theme?
            </div>
          </div>
          <div className="flex justify-end">
            <div
              className={cn("max-w-[70%] px-3 py-1.5 text-xs text-white shadow-sm", radiusOwn)}
              style={{ backgroundColor: accent }}
            >
              Loving the colors 💫
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function previewSwatchStyle(theme: ChatTheme): React.CSSProperties {
  const style: React.CSSProperties = {};
  const bg = getBackgroundStyle(theme);
  Object.assign(style, bg);
  if (!bg.background && !bg.backgroundColor) style.background = "#111417";
  if (theme.accentColor) {
    style.boxShadow = `inset 0 0 0 2px ${theme.accentColor}55`;
  }
  return style;
}

function hexToRgba(hex: string, alpha: number): string | null {
  const trimmed = hex.replace("#", "").trim();
  if (!/^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimmed)) return null;
  const full = trimmed.length === 3
    ? trimmed.split("").map((c) => c + c).join("")
    : trimmed;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function getBackgroundStyle(theme: ChatTheme | undefined): React.CSSProperties {
  if (!theme) return {};
  switch (theme.backgroundType) {
    case "solid":
      return theme.backgroundColor ? { backgroundColor: theme.backgroundColor } : {};
    case "gradient":
      return theme.backgroundGradient ? { background: theme.backgroundGradient } : {};
    case "image": {
      if (!theme.backgroundImage) return {};
      const alpha = Math.max(0, Math.min(100, theme.backgroundOverlayOpacity ?? 0)) / 100;
      const overlayHex = theme.backgroundOverlayColor;
      const overlayRgba = alpha > 0 && overlayHex ? hexToRgba(overlayHex, alpha) : null;
      if (overlayRgba) {
        return {
          background: `linear-gradient(${overlayRgba}, ${overlayRgba}), url(${theme.backgroundImage}) center/cover no-repeat`,
        };
      }
      return {
        backgroundImage: `url(${theme.backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      };
    }
    default:
      return {};
  }
}
